import * as palette from "@govlaunch/palette";
import { Button, LoadingIndicator, Filestack } from "@govlaunch/core";
import { Margin, SpacingBetween } from "~/components/spacings";
import DraggableCover from "~/components/cover/DraggableCover";
import ProgressiveImage from "react-progressive-image";
import React from "react";
import { State } from "react-powerplug";
import batchPreload from "~/lib/utils/batchPreload";
import get from "lodash/fp/get";
import { FormattedMessage } from "react-intl";
import { CameraIcon, RemoveIcon, RepositionIcon } from "~/components/icons/CoverIcons";

export const PLACEHOLDER_COVER = "https://static.govlaunch.com/covers/cover.png";

batchPreload([PLACEHOLDER_COVER]);

interface ICoverProps {
  alt: string;
  width?: number;
  height?: number;
  readOnly?: boolean;
  originalSource?: string;
  repositionedSource?: string;
  placeholderSource?: string;
  containerProps?: any;
  imageProps?: any;
  onChange: (url: string) => any;
  onReposition: (originalSource: string, cropState: any) => any;
  onClick?: (e: React.SyntheticEvent) => any;
  onRemove: (e: React.SyntheticEvent) => any;
}

interface IRepositioning {
  isRepositioning: boolean;
  lastCropState: any | null;
}

const Cover: React.FunctionComponent<ICoverProps> = ({
  originalSource,
  repositionedSource,
  placeholderSource,
  alt,
  readOnly = true,
  width = 1140,
  height = 245,
  onChange,
  onReposition,
  onRemove,
  containerProps,
  imageProps,
}) => {
  batchPreload([originalSource, placeholderSource]);

  if (!originalSource) {
    return (
      <div
        css={{
          display: "flex",
          position: "relative",
          maxWidth: width,
          height: height,
        }}
        {...containerProps}
      >
        <img
          src={placeholderSource}
          alt={alt}
          css={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
            userSelect: "none",
          }}
          {...imageProps}
        />

        {readOnly === false && (
          <div
            css={{
              bottom: 10,
              right: 10,
              position: "absolute",
              pointerEvents: "all",
            }}
          >
            <Filestack
              options={{
                transformations: {
                  rotate: false,
                  circle: false,
                  crop: false,
                },
                storeTo: {
                  location: "s3",
                  access: "public",
                },
              }}
              onSuccess={(result: any) => {
                if (result.filesUploaded.length > 0) {
                  onChange(result.filesUploaded[0].url);
                }
              }}
              customRender={({ onPick }: any) => (
                <Button
                  onClick={onPick}
                  theme="primary"
                  color={palette.white}
                  textColor={palette.innovatorBlue}
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CameraIcon />
                  <Margin ml={7.5}>
                    <FormattedMessage defaultMessage="Edit Cover" id="c56nZj" />
                  </Margin>
                </Button>
              )}
            />
          </div>
        )}
      </div>
    );
  } else if (readOnly) {
    return (
      <div
        css={{
          position: "relative",
          maxWidth: width,
          height: height,
        }}
        {...containerProps}
      >
        <img
          src={repositionedSource || originalSource}
          alt={alt}
          css={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
            userSelect: "none",
          }}
          {...imageProps}
        />
      </div>
    );
  }

  return (
    <State<IRepositioning>
      initial={{
        isRepositioning: false,
        lastCropState: null,
      }}
    >
      {({ state, setState }) => {
        if (state.isRepositioning) {
          return (
            <DraggableCover disabled={false}>
              {({ parentRef, coverRef, events, style, cropState }) => (
                <div
                  ref={parentRef}
                  {...containerProps}
                  style={{
                    maxWidth: width,
                    height,
                    position: "relative",
                    overflow: "hidden",
                    ...get("style", containerProps),
                  }}
                >
                  <img
                    {...events}
                    ref={coverRef}
                    src={originalSource}
                    alt={alt}
                    draggable={false}
                    width="100%"
                    {...imageProps}
                    style={{
                      ...style,
                      ...get("style", imageProps),
                    }}
                  />

                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      position: "absolute",
                      top: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      css={{
                        width: "100%",
                        height: 50,
                        background: "rgba(33, 33, 33, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#ffffff",
                        fontWeight: 500,
                        fontSize: 14,
                        textTransform: "uppercase",
                      }}
                    >
                      <FormattedMessage defaultMessage="Drag to Reposition" id="IQ3n5u" />
                    </div>

                    <div
                      css={{
                        bottom: 10,
                        right: 10,
                        display: "flex",
                        position: "absolute",
                        pointerEvents: "all",
                      }}
                    >
                      <Margin mr={10}>
                        <Button
                          theme="primary"
                          onClick={() => {
                            if (cropState === null) {
                              setState({
                                isRepositioning: false,
                                lastCropState: null,
                              });

                              return;
                            }

                            onReposition(originalSource, cropState);

                            setState({
                              isRepositioning: false,
                              lastCropState: cropState,
                            });
                          }}
                        >
                          <FormattedMessage defaultMessage="Save" id="jvo0vs" />
                        </Button>
                      </Margin>

                      <Button
                        theme="primary"
                        color={palette.white}
                        textColor={palette.innovatorBlue}
                        onClick={() => {
                          setState({
                            isRepositioning: false,
                          });
                        }}
                      >
                        <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DraggableCover>
          );
        } else {
          let source, placeholder;

          if (repositionedSource) {
            // if we have a `repositionedSource` we also have `originalSource`
            source = repositionedSource;
            placeholder = originalSource;
          } else if (originalSource) {
            // if there's a `originalSource` we should have the placeholder as fallback
            source = originalSource;
            placeholder = placeholderSource;
          }

          return (
            <ProgressiveImage src={source as string} placeholder={placeholder as string}>
              {(src: string, loading: boolean) => {
                const didCrop = state.lastCropState !== null;

                if (loading) {
                  return (
                    <div
                      css={{
                        position: "relative",
                        overflow: "hidden",
                        maxWidth: width,
                        height,
                      }}
                      {...containerProps}
                    >
                      {didCrop ? (
                        <img
                          src={originalSource}
                          alt={alt}
                          css={{
                            width: "100%",
                            userSelect: "none",
                            position: "absolute",
                            top: state.lastCropState.y * -1,
                          }}
                          {...imageProps}
                        />
                      ) : (
                        <img
                          src={src}
                          alt={alt}
                          css={{
                            width: "100%",
                            position: "absolute",
                            top: 0,
                            left: 0,
                          }}
                          {...imageProps}
                        />
                      )}

                      {didCrop && (
                        <div
                          css={{
                            top: 0,
                            display: "flex",
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                          }}
                        >
                          <div
                            css={{
                              width: "100%",
                              height: 50,
                              background: "rgba(33, 33, 33, 0.5)",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              color: "#ffffff",
                              fontWeight: 500,
                              fontSize: 14,
                              textTransform: "uppercase",
                            }}
                          >
                            <LoadingIndicator />
                            <Margin ml={7.5}>
                              {didCrop ? (
                                <FormattedMessage defaultMessage="Saving" id="QLkzoS" />
                              ) : (
                                <FormattedMessage defaultMessage="Loading" id="iFsDVR" />
                              )}
                            </Margin>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    css={{
                      position: "relative",
                      maxWidth: width,
                      height: height,
                    }}
                    {...containerProps}
                  >
                    <img
                      src={src}
                      alt={alt}
                      css={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                        userSelect: "none",
                      }}
                      {...imageProps}
                    />

                    <div
                      css={{
                        bottom: 10,
                        right: 10,
                        display: "flex",
                        position: "absolute",
                        pointerEvents: "all",
                      }}
                    >
                      <div
                        css={{
                          display: "flex",
                        }}
                      >
                        <SpacingBetween right={10}>
                          <Button
                            theme="primary"
                            color={palette.white}
                            textColor={palette.innovatorBlue}
                            onClick={() => {
                              setState({
                                isRepositioning: true,
                              });
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <RepositionIcon />
                            <Margin ml={7.5}>
                              <FormattedMessage defaultMessage="Reposition" id="olI5br" />
                            </Margin>
                          </Button>

                          <Button
                            theme="primary"
                            color={palette.white}
                            textColor={palette.orange}
                            onClick={onRemove}
                            style={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <RemoveIcon />
                            <Margin ml={7.5}>
                              <FormattedMessage defaultMessage="Remove" id="G/yZLu" />
                            </Margin>
                          </Button>
                        </SpacingBetween>
                      </div>
                    </div>
                  </div>
                );
              }}
            </ProgressiveImage>
          );
        }
      }}
    </State>
  );
};

export function makeImaginaryUrls(src: string, crop: any) {
  const imaginary = "https://imaginary-ssl.govlaunch.com";

  const { rect, proportional, x, y } = crop;
  const proportionalUrl = `${imaginary}/resize?width=${proportional.width}&height=${proportional.height}&url=${src}&force=true`;

  const croppedUrl = [
    `${imaginary}/extract?`,
    `&areawidth=${rect.width}&areaheight=${rect.height}`,
    `&top=${y}&left=${x}`,
    `&url=${encodeURIComponent(proportionalUrl)}`,
  ].join("");

  return {
    original: src,
    proportional: proportionalUrl,
    cropped: croppedUrl,
  };
}

export default Cover;
